import { createTheme, alpha } from '@mui/material/styles';
import { colors, typography, spacing, borderRadius } from './theme';

// Balanced font scaling factor for clean, premium typography (100% bigger font size)
const scaleFactor = 2.0;

const adminTheme = createTheme({
    palette: {
        primary: {
            main: '#2563EB', // Modern Indigo/Blue
            light: '#60A5FA',
            dark: '#1E40AF',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#7C3AED', // Modern Violet
            light: '#A78BFA',
            dark: '#5B21B6',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#F8FAFC', // Slate 50
            paper: '#FFFFFF',
        },
        text: {
            primary: '#1E293B', // Slate 800
            secondary: '#64748B', // Slate 500
        },
        divider: '#E2E8F0', // Slate 200
    },
    typography: {
        fontFamily: '"Inter", "system-ui", "-apple-system", sans-serif',
        h1: {
            fontSize: `calc(3rem * ${scaleFactor})`,
            fontWeight: 800,
            letterSpacing: '-0.025em',
        },
        h2: {
            fontSize: `calc(2.25rem * ${scaleFactor})`,
            fontWeight: 700,
            letterSpacing: '-0.025em',
        },
        h3: {
            fontSize: `calc(1.875rem * ${scaleFactor})`,
            fontWeight: 700,
        },
        h4: {
            fontSize: `calc(1.5rem * ${scaleFactor})`,
            fontWeight: 600,
        },
        h5: {
            fontSize: `calc(1.25rem * ${scaleFactor})`,
            fontWeight: 600,
        },
        h6: {
            fontSize: `calc(1.125rem * ${scaleFactor})`,
            fontWeight: 600,
        },
        body1: {
            fontSize: `calc(1rem * ${scaleFactor})`, // 1.5rem = 24px
            lineHeight: 1.6,
        },
        body2: {
            fontSize: `calc(0.875rem * ${scaleFactor})`, // 1.31rem = ~21px
            lineHeight: 1.6,
        },
        subtitle1: {
            fontSize: `calc(1rem * ${scaleFactor})`,
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: `calc(0.875rem * ${scaleFactor})`,
            fontWeight: 500,
        },
        button: {
            fontSize: `calc(0.875rem * ${scaleFactor})`,
            fontWeight: 600,
            textTransform: 'none',
        },
        caption: {
            fontSize: `calc(0.75rem * ${scaleFactor})`,
        },
        overline: {
            fontSize: `calc(0.75rem * ${scaleFactor})`,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
    },
    shape: {
        borderRadius: 16, // Softer rounded corners
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: '#F8FAFC',
                    scrollBehavior: 'smooth' as const,
                    '& ::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '& ::-webkit-scrollbar-track': {
                        background: '#F1F5F9',
                    },
                    '& ::-webkit-scrollbar-thumb': {
                        background: '#CBD5E1',
                        borderRadius: '4px',
                    },
                    '& ::-webkit-scrollbar-thumb:hover': {
                        background: '#94A3B8',
                    },
                },
                // Scaling text but specifically targeting non-numeric content is hard, 
                // but we can use oldstyle-nums to make numbers feel more integrated.
                'span, p, h1, h2, h3, h4, h5, h6, div, td, th': {
                    fontVariantNumeric: 'oldstyle-nums',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    padding: '10px 20px',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 20,
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                    border: '1px solid #E2E8F0',
                    '&:hover': {
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                    },
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#0F172A', // Very dark blue/slate for sidebar
                    color: '#F8FAFC',
                    borderRight: 'none',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha('#FFFFFF', 0.8),
                    backdropFilter: 'blur(8px)',
                    color: '#1E293B',
                    boxShadow: 'none',
                    borderBottom: '1px solid #E2E8F0',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    margin: '4px 12px',
                    '&.Mui-selected': {
                        backgroundColor: alpha('#3B82F6', 0.1),
                        color: '#3B82F6',
                        '& .MuiListItemIcon-root': {
                            color: '#3B82F6',
                        },
                        '&:hover': {
                            backgroundColor: alpha('#3B82F6', 0.2),
                        },
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: '#94A3B8',
                    minWidth: 40,
                },
            },
        },
        MuiTable: {
            defaultProps: {
                size: 'small', // Makes all Admin tables dense by default
            },
            styleOverrides: {
                root: {
                    width: '40%', // reduce the width of the columns by 60% (leaving 40%)
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    overflowX: 'auto',
                    width: '100%',
                    display: 'block',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: `calc(1.1rem * ${scaleFactor})`,
                    padding: '8px 8px', // Tighter columns
                    // Globally enhance all typography specifically when inside a table cell
                    '& .MuiTypography-body1': { fontSize: `calc(1.15rem * ${scaleFactor})` },
                    '& .MuiTypography-body2': { fontSize: `calc(1.1rem * ${scaleFactor})` },
                    '& .MuiTypography-caption': { fontSize: `calc(0.9rem * ${scaleFactor})`, display: 'block' },
                    '& .MuiChip-root': { fontSize: `calc(0.9rem * ${scaleFactor})`, height: '32px' },
                },
                head: {
                    fontWeight: 800,
                    backgroundColor: '#F8FAFC',
                    color: '#64748B',
                    textTransform: 'uppercase',
                    fontSize: `calc(0.9rem * ${scaleFactor})`,
                    letterSpacing: '0.05em',
                    padding: '8px 8px',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                    },
                },
            },
        },
    },
});

export default adminTheme;
